import { FC, ReactNode, useRef, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { dollars } from "./dollars";
import BottomSheet from "./BottomSheet";
import ProductDetails from "./ProductDetails";
import { iconFor } from "./ExtrasStep";

type Detail = { title: string; body: any };

const Row: FC<{
  name: string;
  blurb?: ReactNode;
  price: string;
  unit: string;
  on: boolean;
  /** A required extra: it is on and stays on, so the row says Included where the button would be. */
  required?: boolean;
  onToggle: () => void;
  onDetails?: () => void;
}> = ({ name, blurb, price, unit, on, required, onToggle, onDetails }) => {
  const Icon = iconFor(name);
  return (
    <li className={`flex items-center gap-4 p-4 ${on && !required ? "bg-primary-6000/5" : ""}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-secondary-100 text-text-color/80">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <span className="block break-words text-base font-semibold leading-tight">{name}</span>
        {blurb && <span className="mt-0.5 block break-words text-sm text-text-color/70">{blurb}</span>}
        {onDetails && (
          <button
            type="button"
            onClick={onDetails}
            aria-label={`Details: ${name}`}
            className="mt-1 text-sm font-medium underline underline-offset-2"
          >
            Details
          </button>
        )}
      </div>
      <span className="shrink-0 text-right text-lg font-semibold tabular-nums">
        {price}
        <small className="text-sm font-normal text-text-color/60"> / {unit}</small>
      </span>
      <div className="flex w-28 shrink-0 justify-end">
        {required ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-secondary-100 px-3 py-1 text-sm font-medium">
            <CheckIcon className="h-4 w-4 text-primary-6000" aria-hidden="true" />
            Included
          </span>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            aria-pressed={on}
            aria-label={`${on ? "Remove" : "Add"} ${name}`}
            className={`inline-flex h-10 min-w-[88px] items-center justify-center rounded-lg px-4 text-sm font-medium ${
              on ? "border border-text-color/25 text-text-color hover:bg-secondary-50" : "bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary text-on-button"
            }`}
          >
            {on ? "Remove" : "Add"}
          </button>
        )}
      </div>
    </li>
  );
};

/**
 * Step 3 of the desktop booking: extras as rows (1NOW-11174, replacing the cards of 1NOW-11173), the Bonzah
 * add-ons first, then the car's own extras. Each row has an icon, the name, a one-line description, the price
 * and Add or Remove, written through the page's own handlers, so a Bonzah add-on still asks for the Bonzah terms
 * first. A required extra is on and stays on: it says Included in place of the button. There is no quantity: a
 * booking carries each extra once (the booking call sends their ids), so there is no minus or plus to draw.
 */
const DesktopExtras: FC<{
  extras: any[];
  addons: any[];
  selectedExtras: Set<any>;
  onExtra: (id: any, on: boolean) => void;
  selectedAddons: Set<any>;
  /** attemptAddonSelection: toggles, behind the Bonzah terms the first time. */
  onAddon: (value: string) => void;
  bgColor?: string;
}> = ({ extras, addons, selectedExtras, onExtra, selectedAddons, onAddon, bgColor }) => {
  const [detail, setDetail] = useState<Detail | null>(null);
  const lastDetail = useRef<Detail | null>(null);
  if (detail) lastDetail.current = detail;
  const shown = detail ?? lastDetail.current;

  if (extras.length === 0 && addons.length === 0)
    return <p className="rounded-xl border border-secondary-100 p-4 text-sm">This car has no extras to add.</p>;

  return (
    <>
      <ul className="m-0 list-none overflow-hidden rounded-xl border border-secondary-100 bg-card-color p-0 divide-y divide-secondary-100">
        {addons.map((a) => {
          const v = a.value?.[0];
          return (
            <Row
              key={`addon-${a.title}`}
              name={a.title}
              price={dollars(a.price)}
              unit="day"
              on={selectedAddons.has(v)}
              onToggle={() => onAddon(v)}
              onDetails={() => setDetail({ title: a.title, body: a })}
            />
          );
        })}
        {extras.map((x) => (
          <Row
            key={`extra-${x.id}`}
            name={x.name}
            blurb={
              x.description ? (
                <span className="whitespace-pre-line">{x.description}</span>
              ) : x.is_recommended === true && !x.is_required ? (
                "Recommended"
              ) : undefined
            }
            price={dollars(x.amount)}
            unit={x.nature === "per_day" ? "day" : "trip"}
            on={selectedExtras.has(x.id)}
            required={x.is_required}
            onToggle={() => onExtra(x.id, !selectedExtras.has(x.id))}
          />
        ))}
      </ul>
      <BottomSheet
        drawer
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={shown?.title ?? "Details"}
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setDetail(null)}
              className="min-h-[44px] rounded-lg border border-text-color/25 px-5 text-sm font-medium"
            >
              Close
            </button>
          </div>
        }
      >
        {shown && <ProductDetails product={shown.body} bgColor={bgColor} bonzahLinks />}
      </BottomSheet>
    </>
  );
};

export default DesktopExtras;
